/**
 * Environment Configuration Utility
 * Provides safe access to environment variables across all contexts
 */

/**
 * Get backend URL with multiple fallback strategies
 * Works in browser, mobile, and all deployment contexts
 */
export const getBackendURL = () => {
  // Strategy 1: Try window._env_ (runtime configuration - HIGHEST PRIORITY)
  if (typeof window !== 'undefined' && 
      window._env_ && 
      window._env_.REACT_APP_BACKEND_URL) {
    return window._env_.REACT_APP_BACKEND_URL;
  }
  
  // Strategy 2: Try window.getBackendURL (from env-config.js)
  if (typeof window !== 'undefined' && 
      typeof window.getBackendURL === 'function') {
    return window.getBackendURL();
  }
  
  // Strategy 3: Try process.env (Create React App standard)
  if (typeof process !== 'undefined' && 
      process.env && 
      process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // Strategy 4: Intelligent origin detection for Emergent platform
  if (typeof window !== 'undefined' && window.location) {
    const origin = window.location.origin;
    const hostname = window.location.hostname;
    
    // Check if running on Emergent platform
    if (hostname.includes('emergentagent.com') || 
        hostname.includes('preview.emergent') ||
        hostname.includes('chef-connect')) {
      return origin;
    }
    
    // Check if localhost with different port
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      // If frontend is on 3000, backend is likely on 8001
      return 'http://localhost:8001';
    }
  }
  
  // Strategy 5: Default fallback
  console.warn('[Config] Backend URL not found, using fallback: http://localhost:8001');
  return 'http://localhost:8001';
};

/**
 * Get any environment variable safely
 */
export const getEnvVar = (key, defaultValue = '') => {
  // Try window._env_ first
  if (typeof window !== 'undefined' && 
      window._env_ && 
      window._env_[key]) {
    return window._env_[key];
  }
  
  // Try process.env
  if (typeof process !== 'undefined' && 
      process.env && 
      process.env[key]) {
    return process.env[key];
  }
  
  // Return default
  return defaultValue;
};

/**
 * Check if running in development mode
 */
export const isDevelopment = () => {
  if (typeof process !== 'undefined' && process.env) {
    return process.env.NODE_ENV === 'development';
  }
  
  if (typeof window !== 'undefined' && window.location) {
    return window.location.hostname === 'localhost' || 
           window.location.hostname === '127.0.0.1';
  }
  
  return false;
};

/**
 * Check if running in production mode
 */
export const isProduction = () => {
  if (typeof process !== 'undefined' && process.env) {
    return process.env.NODE_ENV === 'production';
  }
  
  return !isDevelopment();
};

/**
 * Get full API URL for an endpoint
 */
export const getApiUrl = (endpoint) => {
  const baseUrl = getBackendURL();
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const apiPrefix = cleanEndpoint.startsWith('/api') ? '' : '/api';
  
  return `${baseUrl}${apiPrefix}${cleanEndpoint}`;
};

// Export configuration object
export const config = {
  backendURL: getBackendURL(),
  isDevelopment: isDevelopment(),
  isProduction: isProduction(),
};

// Log configuration for debugging
console.log('[Config] Backend URL:', config.backendURL);
console.log('[Config] Environment:', config.isDevelopment ? 'development' : 'production');

export default config;
